import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const date = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export function Stars({ n, size = "h-4 w-4" }) {
  return (
    <span className="flex gap-0.5" aria-label={`${n} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= n ? "fill-[var(--gold)] text-[var(--gold)]" : "text-[var(--line)]"}`} />)}
    </span>
  );
}

export function useReviews(slug) {
  const { data } = useQuery({ queryKey: ["reviews", slug], queryFn: () => api.get("/reviews", { params: { product: slug } }).then((r) => r.data), enabled: !!slug });
  return Array.isArray(data) ? data : [];
}

export function ProductReviews({ slug }) {
  const reviews = useReviews(slug);
  const { lang } = useLang();
  const en = lang === "en";
  if (!reviews.length) return null;
  const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  return (
    <section data-testid="product-reviews" className="mt-24 border-t border-[var(--line)] pt-16">
      <p className="eyebrow mb-4">{en ? "Customer reviews (in French)" : "Avis clientes"}</p>
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="font-display text-3xl md:text-4xl">{en ? "They tried it" : "Elles ont testé"}</h2>
        <span data-testid="product-reviews-average" className="flex items-center gap-2 text-sm text-[var(--parchment)]/90">
          <Stars n={Math.round(avg)} /> {avg.toFixed(1).replace(".", ",")} / 5 · {reviews.length} avis
        </span>
      </div>
      <ul className="mt-10 grid gap-6 md:grid-cols-2">
        {reviews.map((r) => (
          <li key={r.id} data-testid={`product-review-${r.id}`} className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6">
            <div className="flex items-center justify-between gap-3"><Stars n={r.rating} />
              <span className="text-xs text-[var(--parchment)]/70">{date(r.received_at)}</span></div>
            <p className="mt-4 text-sm leading-relaxed text-[var(--parchment)]/90 md:text-base">« {r.text} »</p>
            <p className="mt-4 flex flex-wrap items-center gap-3 text-sm font-semibold">{r.name}
              {r.verified ? <span data-testid="review-verified" className="flex items-center gap-1 text-xs font-normal text-[var(--gold-soft)]"><BadgeCheck className="h-4 w-4" /> {en ? "Verified purchase" : "Achat vérifié"}</span> : null}</p>
          </li>
        ))}
      </ul>
      <p data-testid="product-reviews-info" className="mt-6 text-xs text-[var(--parchment)]/75">{en ? "Reviews collected by email from customers who ordered on ozentya.net, published with their consent, unpaid and unedited, newest first. “Verified purchase”: review linked to a paid order of this product." : "Avis recueillis par email auprès de clientes ayant commandé sur ozentya.net, publiés avec leur accord, sans contrepartie et sans modification du texte, du plus récent au plus ancien. « Achat vérifié » : avis rattaché à une commande payée de ce produit."}</p>
    </section>
  );
}
